import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { medicalDocumentsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Heart,
  AlertTriangle,
  Pill,
  History,
  Droplets,
  Stethoscope,
  FileText,
  Calendar,
  Loader2,
  Download,
  Shield,
  Clock,
  User,
  XCircle,
  Lock
} from 'lucide-react';

const SharedMedicalProfile = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  useEffect(() => {
    loadSharedProfile();
  }, [token]);

  const loadSharedProfile = async () => {
    try {
      const response = await medicalDocumentsAPI.getSharedProfile(token);
      setData(response.data);
    } catch (err) {
      console.error('Error loading shared profile:', err);
      if (err.response?.status === 404) {
        setError('Ce lien de partage est invalide ou a ete revoque.');
      } else if (err.response?.status === 410) {
        setError(err.response?.data?.detail || 'Ce lien a expire.');
      } else {
        setError('Erreur lors du chargement du dossier medical.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const response = await medicalDocumentsAPI.getSharedProfilePDF(token);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `DOCKHTA_Synthese_Partagee_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#1976D2] mx-auto mb-4" />
          <p className="text-slate-600">Chargement du dossier medical...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Lien invalide</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              Retour a l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { patient, profile, recent_consultations, clinical_notes, share_info } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1976D2] to-[#1565C0] text-white py-6">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4" />
            <span className="text-sm opacity-90">Dossier medical partage securise</span>
          </div>
          <h1 className="text-2xl font-bold">DOCKHTA</h1>
          <p className="text-blue-100 text-sm mt-1">Synthese medicale</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 pb-24">
        {/* Share info banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 text-amber-800">
            <Clock className="w-4 h-4" />
            <span className="text-sm">
              Ce lien expire le {new Date(share_info.expires_at).toLocaleDateString('fr-FR')} 
              {' '}a {new Date(share_info.expires_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <p className="text-xs text-amber-600 mt-1">
            Consultation {share_info.access_count}/10
          </p>
        </div>

        {/* Patient Info */}
        <Card className="mb-6 border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#1976D2] rounded-full flex items-center justify-center text-white text-xl font-bold">
                {patient?.full_name?.charAt(0) || 'P'}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">{patient?.full_name || 'Patient'}</h2>
                {patient?.gender && (
                  <p className="text-slate-500">{patient.gender === 'male' ? 'Homme' : 'Femme'}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Medical Profile */}
        <Card className="mb-6 border-0 shadow-md bg-gradient-to-br from-white to-blue-50">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-[#1976D2]" />
              Informations medicales
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {/* Blood Type */}
              <div className="flex items-start gap-3">
                <Droplets className="w-5 h-5 text-red-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Groupe sanguin</p>
                  <p className="font-medium text-slate-900">
                    {profile?.blood_type || <span className="text-slate-400">Non renseigne</span>}
                  </p>
                </div>
              </div>

              {/* Allergies */}
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Allergies</p>
                  {profile?.allergies?.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {profile.allergies.map((a, i) => (
                        <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                          {a}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400">Aucune</p>
                  )}
                </div>
              </div>

              {/* Known Conditions */}
              <div className="flex items-start gap-3">
                <Heart className="w-5 h-5 text-purple-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Maladies connues</p>
                  {profile?.known_conditions?.length > 0 ? (
                    <p className="font-medium text-slate-900">{profile.known_conditions.join(', ')}</p>
                  ) : (
                    <p className="text-slate-400">Aucune</p>
                  )}
                </div>
              </div>

              {/* Current Treatments */}
              <div className="flex items-start gap-3">
                <Pill className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Traitements actuels</p>
                  {profile?.current_treatments?.length > 0 ? (
                    <p className="font-medium text-slate-900">{profile.current_treatments.join(', ')}</p>
                  ) : (
                    <p className="text-slate-400">Aucun</p>
                  )}
                </div>
              </div>

              {/* Medical History */}
              <div className="flex items-start gap-3 md:col-span-2">
                <History className="w-5 h-5 text-slate-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Antecedents</p>
                  {profile?.medical_history?.length > 0 ? (
                    <p className="font-medium text-slate-900">{profile.medical_history.join(', ')}</p>
                  ) : (
                    <p className="text-slate-400">Aucun</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Consultations */}
        {recent_consultations?.length > 0 && (
          <Card className="mb-6 border-0 shadow-md">
            <CardContent className="p-5">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                <Stethoscope className="w-5 h-5 text-blue-600" />
                Consultations recentes
              </h3>
              <div className="space-y-3">
                {recent_consultations.map((apt, i) => (
                  <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {apt.professional_name} - {apt.type}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(apt.date).toLocaleDateString('fr-FR')}
                        {apt.diagnosis && ` • ${apt.diagnosis}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Clinical Notes */}
        {clinical_notes?.length > 0 && (
          <Card className="mb-6 border-0 shadow-md">
            <CardContent className="p-5">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-amber-600" />
                Notes cliniques
              </h3>
              <div className="space-y-3">
                {clinical_notes.map((note, i) => (
                  <div key={i} className="bg-amber-50 rounded-lg p-3">
                    <p className="text-sm font-medium text-slate-900">{note.title}</p>
                    <p className="text-xs text-slate-500">
                      Par {note.professional_name} • {new Date(note.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Download PDF */}
        <Button
          onClick={handleDownloadPDF}
          disabled={downloadingPDF}
          className="w-full bg-[#1976D2] hover:bg-[#1565C0] h-12 rounded-xl"
        >
          {downloadingPDF ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Download className="w-5 h-5 mr-2" />
          )}
          Telecharger en PDF
        </Button>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-slate-400">
          <p>Document partage via DOCKHTA</p>
          <p>www.dockhta.com</p>
        </div>
      </div>
    </div>
  );
};

export default SharedMedicalProfile;
